// PoliceReportList.hook.js
import { useRef, useContext, useEffect, useState } from 'react';
import { AppContext } from 'App.context';
import { ApiContext } from 'utils/networking/Api.context';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { saveAs } from 'file-saver';
import { getParsedReports } from '../../civitfun-police-report-utils/BookingReportParse.utils';
import FIELDS from '../../civitfun-police-report-utils/PoliceReportFields';
import Colors from 'constants/Colors';
import constants from 'utils/constants';

export default function usePoliceReportListHook() {
  const {
    appState,
    appActions: { getSelectedHotel, translate, setPoliceReportFilters, getSelectedLanguage },
  } = useContext(AppContext);

  const { API } = useContext(ApiContext);
  const navigate = useNavigate();
  const timeoutRef = useRef(null);

  const [state, setState] = useState({
    isLoading: false,
    isLoadingDashboard: false,
    dataSource: { items: [], totalItems: 0, totalPages: 0, page: 0, size: 10 },
    date: moment().subtract(1, 'days'),
    filters: null,
    platform: null,
    suscription: null,
    ftpErrors: [],
    isReportLoading: false,
    isForcedPoliceReport: false,
    showAllDayDeleteWarning: false,
    statusSendReport: null,
    InterfaceType: null,
    sendTimestamp: null,
    fileSearchError: null,
    hasErtzFileConfig: false,
  });

  // Initial load
  useEffect(() => {
    actions.loadPoliceReportConfig();
  }, [appState.selectedHotel]);

  // Load reports when filters/date/page change
  useEffect(() => {
    actions.getPoliceReports();
  }, [, state.filters, state.date, appState.selectedHotel]);

  // Handle isReportLoading like the second hook
  useEffect(() => {
    if (state.isReportLoading) {
      updateState({
        isLoading: true,
        dataSource: { ...state.dataSource, items: [] },
      });
      actions.retryGetAfterTimeout();
    } else {
      updateState({ isLoading: false });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [state.isReportLoading]);

  const actions = {
    loadPoliceReportConfig: () => {
      API.getBookingReportConfig({ hotelId: getSelectedHotel()?.key }).then(res => {
        let InterfaceType;
        switch (res?.bookingProvider) {
          case constants.BOOKING_PROVIDER.SFTP:
            InterfaceType = 'SFTP';
            break;
          case constants.BOOKING_PROVIDER.S3:
            InterfaceType = 'Civitfun SFTP';
            break;
          default:
            InterfaceType = 'PMS';
            break;
        }

        updateState({
          InterfaceType,
          suscription: res?.suscription === 'active',
        });
      });

      API.getPoliceConfig({ hotelId: getSelectedHotel()?.key })
        .then(res => {
          updateState({
            platform: res.platform,
            hasErtzFileConfig: !!res?.awsKey,
          });
        })
        .catch(() => updateState({ isLoading: false }));
    },

    getPoliceReports: () => {
      const hotel = getSelectedHotel();

      API.getBookingReportList({
        hotelId: hotel.id || hotel.key,
        bookingDate: moment(state.date).format('YYYY-MM-DD'),
        page: state.dataSource.page,
        size: state.dataSource.size,
        status: state.filters?.status,
        bookingCode: state.filters?.bookingCode,
        batch: state.filters?.batch,
        name: state.filters?.name,
        surname: state.filters?.surname,
      })
        .then(res => {
          actions.setReportStatus(res);

          if (!res.items || res.items.length === 0) {
            updateState({ dataSource: { ...res, items: [] } });
          } else {
            const enrichedItems = res.items.map(item => ({
              ...item,
              resvStatus: item.resvStatus || (item.booking?.resvStatus ?? null),
            }));

            updateState({
              isLoading: state.isReportLoading,
              dataSource: { ...res, items: enrichedItems },
            });
          }
        })
        .catch(err => {
          console.error('Error obtaining police reports:', err);
          updateState({ isLoading: false });
        });
    },

    forcePoliceReportDownloadTrigger: async () => {
      if (state.dataSource?.totalItems > 0) {
        notification.warning({ message: translate('policeReport.forceDownloadTriggerWarning') });
        return;
      }
      const hotelId = getSelectedHotel()?.id ?? getSelectedHotel()?.key;
      const date = moment(state.date).format('YYYY-MM-DD');

      updateState({ isReportLoading: true });
      try {
        await API.forceBookingReport({ hotelId, date });
        notification.success({ message: translate('policeReport.forceDownloadTriggerInfo') });
        updateState({ isForcedPoliceReport: true, statusSendReport: 'loading' });
      } catch {
        notification.error({ message: translate('policeReport.forceDownloadTriggerError') });
      }
    },

    sendGetFiles: () => {
      const date = moment(state.date).format('YYYY-MM-DD');
      const hotelId = getSelectedHotel()?.key;
      API.bookingReportgetDownloadFiles({ hotelId, date })
        .then(res => {
          if (res?.file?.data) {
            const blob = new Blob([new Uint8Array(res.file.data)], { type: 'application/zip' });
            saveAs(blob, res.fileName);
          } else {
            notification.error({ message: translate('policeReport.downloadError') });
          }
        })
        .catch(err => {
          return notification.error({ message: translate('policeReport.downloadError') });
        });
    },

    sendAllErrors: () => {
      const body = {
        date: moment(state.date).format('YYYY-MM-DD'),
        hotelId: getSelectedHotel()?.key,
      };
      API.policeReportSendAllErrors(body)
        .then(() => {
          setTimeout(() => {
            notification.success({ message: translate('policeReport.sendAllErrorsSuccess') });
            actions.getPoliceReports();
          }, 20000);
        })
        .catch(() => notification.error({ message: translate('policeReport.sendAllErrorsError') }));
    },

    onDeleteAllDayClicked: () => updateState({ showAllDayDeleteWarning: true }),
    setShowDeleteAllWarning: show => updateState({ showAllDayDeleteWarning: show }),

    sendDeleteAll: () => {
      const data = {
        allDay: true,
        date: moment(state.date).format('YYYY-MM-DD'),
        hotelId: getSelectedHotel().key,
      };
      API.policeReportSendDelete(data)
        .then(() => {
          setTimeout(() => {
            notification.success({ message: translate('policeReport.delete.success') });
            actions.getPoliceReports();
          }, 1000);
          updateState({ isLoading: false, showAllDayDeleteWarning: false });
        })
        .catch(() => {
          notification.error({ message: translate('policeReport.delete.error') });
          updateState({ isLoading: false, showAllDayDeleteWarning: false });
        });
    },

    getColorForStatus: status => {
      switch (status) {
        case 'ok':
          return Colors.testOk;
        case 'error':
          return Colors.red;
        case 'pending':
          return Colors.yellowPill;
        default:
          return Colors.lightGray;
      }
    },

    parseError: (error, object) => {
      if (error === undefined && !object.batch) {
        return translate('policeReport.erronInTheReport');
      } else if (error === undefined && object.batch) {
        return translate('policeReport.errorOnTargetPlatform');
      }

      try {
        let newString = error;
        newString = newString.replace(
          'Falta el campo requerido:',
          translate('policeReport.detailFields.requiredFields')
        );
        newString = newString.replace(
          'Error de validación del campo:',
          translate('policeReport.detailFields.fieldValidation')
        );
        Object.values(FIELDS).forEach(key => {
          const regex = new RegExp(`\\b${key}\\b`, 'g');
          newString = newString.replace(regex, translate(`policeReport.detailFields.${key}`));
        });
        return newString;
      } catch {
        return error;
      }
    },

    onDateSet: date => updateState({ date, isForcedPoliceReport: false }),

    onRowClicked: data => {
      const platform = state.platform;
      data.platform = platform || '';
      navigate(`/${platform.toLowerCase()}/police-report/RH/${data.id}`, {
        state: { report: data },
      });
      setPoliceReportFilters(state);
    },

    onChange: filters => {
      let newPage = state.dataSource.page;
      let newPageSize = state.dataSource.size;
      let searchFilters;

      if (state.dataSource?.page !== filters?.pagination.page) newPage = filters?.pagination.page;
      if (state.dataSource?.size !== filters?.pagination.pageSize)
        newPageSize = filters?.pagination.pageSize;

      if (filters?.where) {
        searchFilters = {
          bookingCode: filters?.where['bookingCode'],
          name: filters?.where['name'],
          surname: filters?.where['surname'],
          status: filters?.where['status'],
          batch: filters?.where['batch'],
        };
      }

      updateState({
        dataSource: { ...state.dataSource, page: newPage, size: newPageSize, items: [] },
        filters: searchFilters,
      });
    },

    retryGetAfterTimeout: () => {
      updateState({ isLoading: true });

      timeoutRef.current = setTimeout(() => {
        actions.getPoliceReports();

        if (
          (state.isReportLoading && state.dataSource.items?.length === 0) ||
          state.dataSource.length === 0
        ) {
          actions.retryGetAfterTimeout();
        }
      }, 5000);
    },
    setReportStatus: response => {
      const items = response?.items;
      const reportStatus = response?.reportStatus;
      const sendTimestamp = response?.reportStatus?.timestamp;
      const fileSearchError = response?.reportStatus?.ErrorMessage;
      const isReportLoading = response?.reportStatus?.isDashboardLoading || false;
      let status = 'notSent'; // Default value

      // If no status on report but there ara bookings set Ok
      if (!reportStatus?.length && items?.length > 0) {
        status = 'ok';
      }

      if (reportStatus?.status) {
        status = reportStatus.status;
      }

      updateState({
        statusSendReport: status,
        sendTimestamp: sendTimestamp,
        fileSearchError: fileSearchError,
        isReportLoading: isReportLoading,
      });
    },
  };

  function updateState(object) {
    setState(prev => ({ ...prev, ...object }));
  }

  return { state, actions };
}
